import React, { useState } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TextInput,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AntDesign, Feather } from '@expo/vector-icons';
import { fetchGroups } from '../services/groupService';
import { selectedGroupAtom } from '../atoms';
import { useTheme } from '../contexts/ThemeContext';
import { Group, ZONE_TYPE_INFO } from '../types';
import CachedImage from '../components/CachedImage';
import PixelContainer from '../components/PixelContainer';
import { fontStyle } from '../config/fonts';

export default function GroupSelectorScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { colors } = useTheme();
  const setSelectedGroup = useSetAtom(selectedGroupAtom);
  const [search, setSearch] = useState('');

  const { data: groups, isLoading } = useQuery({
    queryKey: ['groups', search],
    queryFn: () => fetchGroups({ search }),
    staleTime: 10000,
  });

  const handleSelectGroup = (group: Group) => {
    setSelectedGroup(group);
    router.back();
  };

  const handleClose = () => {
    router.back();
  };

  const renderItem = ({ item }: { item: Group }) => {
    const typeInfo = ZONE_TYPE_INFO[item.type];

    return (
      <Pressable
        style={[styles.groupItem, { borderBottomColor: colors.border }]}
        onPress={() => handleSelectGroup(item)}
      >
        <View style={styles.groupImageContainer}>
          {item.image ? (
            <CachedImage uri={item.image} style={styles.groupImage} />
          ) : (
            <View style={[styles.groupImage, styles.groupImagePlaceholder, { backgroundColor: colors.surface }]}>
              <Feather name="users" size={18} color={colors.textSecondary} />
            </View>
          )}
          <View style={[styles.typeBadge, { backgroundColor: typeInfo.color }]}>
            <Text style={styles.typeBadgeEmoji}>{typeInfo.emoji}</Text>
          </View>
        </View>
        <View style={styles.groupInfo}>
          <Text style={[styles.groupName, fontStyle, { color: colors.text }]}>{item.name}</Text>
          <Text style={[styles.groupMeta, fontStyle, { color: colors.textSecondary }]}>
            {item.membersCount || 0} {(item.membersCount || 0) === 1 ? t('zone.member') : t('zone.members')}
          </Text>
        </View>
      </Pressable>
    );
  };

  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View style={styles.emptyContainer}>
        <Feather name="users" size={40} color={colors.textSecondary} />
        <Text style={[styles.emptyText, fontStyle, { color: colors.textSecondary }]}>{t('zone.noZones')}</Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={['bottom']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <Pressable onPress={handleClose} style={styles.closeButton}>
          <AntDesign name="close" size={22} color={colors.text} />
        </Pressable>
        <Text style={[styles.headerTitle, fontStyle, { color: colors.text }]}>{t('post.postTo')}</Text>
        <View style={styles.closeButton} />
      </View>

      {/* Search */}
      <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.searchContainer}>
        <Feather name="search" size={18} color={colors.textSecondary} />
        <TextInput
          style={[styles.searchInput, fontStyle, { color: colors.text }]}
          placeholder={t('zone.search')}
          placeholderTextColor={colors.textSecondary}
          value={search}
          onChangeText={setSearch}
        />
        {search.length > 0 && (
          <Pressable onPress={() => setSearch('')}>
            <AntDesign name="closecircle" size={16} color={colors.textSecondary} />
          </Pressable>
        )}
      </PixelContainer>

      {/* Groups List */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={groups || []}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={styles.listContent}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingHorizontal: 16,
    flexGrow: 1,
  },
  groupItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  groupImageContainer: {
    position: 'relative',
    marginRight: 12,
  },
  groupImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  groupImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  typeBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#fff',
  },
  typeBadgeEmoji: {
    fontSize: 8,
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  groupMeta: {
    fontSize: 12,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 15,
  },
});
